import { EntityTypeEnum, Workspace } from '@sr-sdks/api-sdk-axios';
import { Alert, Card, CardContent } from '@sr/ui-library';
import { UseFormReturn } from 'react-hook-form';
import { getBusinessNumberLabel } from '../../utils/helpers/common';
import { AbnGstStatusFields } from './fields/abnGstStatus/AbnGstStatusFields';
import { BusinessNumberField } from './fields/businessNumber/BusinessNumberField';
import { BusinessNumberCountryField } from './fields/businessNumberCountry/BusinessNumberCountryField';
import { CategoriesField } from './fields/categories/CategoriesField';
import { EntityNameField } from './fields/entityName/EntityNameField';
import { EntityTypeField } from './fields/entityType/EntityTypeField';
import { RepresentsClientsField } from './fields/representsClients/RepresentsClientsField';
import { TaxResidencyField } from './fields/taxResidency/TaxResidencyField';
import { WorkspaceNameField } from './fields/workspaceName/WorkspaceNameField';
import { WorkspaceDetailsFormValues } from './shared/types';

interface WorkspaceDetailsEntitySectionProps {
  businessNumberCountry: string;
  entityType: string;
  form: UseFormReturn<WorkspaceDetailsFormValues>;
  /**
   * Frozen while the membership is in review (APP-2254). Resolved by
   * `useEntityDetailsLocked` in WorkspaceDetailsForm. Display only — the API
   * enforces the same rule on save, so the submit path needs no flag.
   */
  isLocked?: boolean;
  /**
   * Onboarding step 2. The field locks below are settings-page rules in v2, so
   * onboarding must opt out of them or it locks fields the legacy flow left
   * editable (APP-2237, QA note 4).
   */
  isOnboarding?: boolean;
  workspace: Workspace;
}

/**
 * Entity Details section — pure UI. Owns no form state; receives the shared
 * form instance from WorkspaceDetailsForm.
 */
export const WorkspaceDetailsEntitySection = ({
  businessNumberCountry,
  entityType,
  form,
  isLocked = false,
  isOnboarding = false,
  workspace,
}: WorkspaceDetailsEntitySectionProps) => {
  const businessNumberLabel = getBusinessNumberLabel(businessNumberCountry);
  const isIndividual = entityType === EntityTypeEnum.Individual;
  /**
   * Both view-models are hooks, so both run every render and the flow picks
   * one. They differ in the non-AU case: settings gates editing on the
   * membership stage, onboarding never does.
   */
  const entityNameSettingsProps = EntityNameField.useEntityNamePropsForSettings(
    form.control,
    workspace,
  );
  const entityNameOnboardingProps =
    EntityNameField.useEntityNamePropsForOnboarding(form.control, workspace);
  const entityNameProps = isOnboarding
    ? entityNameOnboardingProps
    : entityNameSettingsProps;

  // Only show workspace-name/entity-name/categories rows once both controlling
  // fields are selected (FR-007).
  const isTruthyBusinessNumberCountryAndEntityType =
    !!businessNumberCountry && !!entityType;

  // Locks entityType: non-Individual AU with entity type already set, regardless of verification.
  // Matches v2: entityType && entityType !== Individual && businessNumberCountry === 'AU'
  // — and, like v2, only on the settings page: onboarding leaves the entity
  // type editable whatever the workspace already holds.
  const isNonIndividualAuBusiness =
    !isOnboarding &&
    Boolean(workspace.entityType) &&
    workspace.entityType !== EntityTypeEnum.Individual &&
    workspace.businessNumberCountry === 'AU';

  // Locks businessNumber: any verified AU business regardless of entity type.
  // Matches v2: businessNumberVerified (in settings page context)
  const isVerifiedAuAbn =
    workspace.businessNumberCountry === 'AU' &&
    Boolean(workspace.businessNumberVerified);

  return (
    <section className="space-y-4">
      <h2 className="text-base font-medium text-neutral-950">Entity Details</h2>

      <Card className="rounded-lg border-neutral-300 shadow-none @container">
        <CardContent className="space-y-6 p-5">
          {isLocked && (
            <Alert title="Membership currently in review" variant="warning">
              You will not be able to edit your entity details while your
              membership is being reviewed for approval.
            </Alert>
          )}

          {/*
            One switch for the whole grid rather than a `disabled` prop per
            field: every control below bottoms out in a native `input` or
            `button`, so the fieldset disables them all — including fields added
            later, which a field-by-field lock would silently miss (APP-2254).

            Controls, `Label` and `Badge` all dim themselves inside a disabled
            fieldset. The status lines carry that rule at their own elements: a
            blanket `[&:disabled_p]` here would also dim `FormMessage`, greying
            out the errors a locked member most needs to read. `min-w-0` opts
            out of the fieldset's UA `min-inline-size: min-content`.
          */}
          <fieldset
            className="grid min-w-0 grid-cols-1 gap-x-4 gap-y-6 @2xl:grid-cols-2"
            disabled={isLocked}
          >
            <BusinessNumberCountryField
              control={form.control}
              disabled={Boolean(workspace.businessNumberCountry)}
            />
            <EntityTypeField
              businessNumberCountry={businessNumberCountry}
              control={form.control}
              disabled={isNonIndividualAuBusiness}
            />

            {/*
              Legacy gates the business-number block on
              `currentCountry && entityType && entityType !== Individual`
              (BusinessDetailsForm.tsx:330-333). Dropping the country/entityType
              half rendered an empty ABN field — and its validation errors —
              before the user had chosen anything.
            */}
            {isTruthyBusinessNumberCountryAndEntityType && !isIndividual && (
              <>
                <BusinessNumberField
                  businessNumberLabel={businessNumberLabel}
                  disabled={isVerifiedAuAbn}
                  form={form}
                />
                <AbnGstStatusFields control={form.control} />
              </>
            )}

            {/* Only shown once both country and entityType are selected (FR-007). */}
            {isTruthyBusinessNumberCountryAndEntityType && (
              <WorkspaceNameField control={form.control} />
            )}
            <EntityNameField control={form.control} {...entityNameProps} />

            {/*
              The category picker sits outside legacy's country/entityType
              conditions (BusinessDetailsForm.tsx:447-490), so it is available
              from the first render.
            */}
            <CategoriesField control={form.control} />

            {isTruthyBusinessNumberCountryAndEntityType && (
              <TaxResidencyField control={form.control} />
            )}

            {isTruthyBusinessNumberCountryAndEntityType && !isIndividual && (
              <RepresentsClientsField control={form.control} />
            )}
          </fieldset>
        </CardContent>
      </Card>
    </section>
  );
};
